import { OptionProps } from '@mui/base';
import { AddExternalUserForm } from 'components/Drawer/types';
import dayjs from 'dayjs';
import { Role } from 'graphql/generated/graphqlTypes';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { sortBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AddExternalUserDrawer } from 'state/types';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import AddExternalUser from 'ui/organisms/Drawer/variants/AddExternalUser';
import {
  handleExpirationDateInputValidation,
  handleNameInputValidation,
  handleSwitchIsAdministratorRoleEnableOnChange,
  handleSwitchIsExpirationDateOnChange
} from '../externalUserGroup/shared';
import { createRoleSelectOptions, emailRegex } from './shared';

const isMultiAdminEnabled = process.env.REACT_APP_IS_MULTI_ADMIN_ENABLED === 'true';

const AddExternalUserVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { register, handleSubmit, setValue, control } = useForm<AddExternalUserForm>();
  const { drawerState } = useDrawer<AddExternalUserDrawer>();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { externalUsersGroups, addOrEditVisitor, addVisitorMemberMutationWithRoleLoading } = useExternalUsers({
    translation,
    handleFetchError
  });
  const { hideDrawer } = useDrawer();
  const [nameInputValue, setNameInputValue] = useState('');
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailInputValidation, setEmailInputValidation] = useState<string | undefined>(undefined);
  const [isExpirationDateEnabled, setIsExpirationDateEnabled] = useState<boolean>();
  const [isAdministratorRoleEnabled, setIsAdministratorRoleEnabled] = useState<boolean>(false);
  const [expirationDateInputValue, setExpirationDateInputValue] = useState<dayjs.Dayjs | null>(null);
  const [expirationDateInputValidation, setExpirationDateInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);
  const [selectValue, setSelectValue] = useState<OptionProps<Role | null>>({
    label: translation.user,
    value: Role.ExternalGroupUser
  });
  const [roleInputValidation, setRoleInputValidation] = useState<string | undefined>(undefined);

  const onSubmit = async () => {
    setShowValidation(true);
    if (
      nameInputValidation === undefined &&
      emailInputValidation === undefined &&
      expirationDateInputValidation === undefined &&
      !addVisitorMemberMutationWithRoleLoading &&
      selectValue &&
      selectValue.value
    ) {
      addOrEditVisitor({
        name: nameInputValue,
        shouldBeAdmin: isAdministratorRoleEnabled,
        visitorGroupMembershipInput: [
          {
            visitorGroupId: drawerState.contentValue.visitorGroupId,
            role: selectValue.value
          }
        ],
        email: emailInputValue || undefined,
        expirationDate:
          !isAdministratorRoleEnabled && isExpirationDateEnabled && expirationDateInputValue
            ? expirationDateInputValue
            : undefined
      });
      hideDrawer();
    }
  };

  useEffect(() => {
    setValue('name', nameInputValue);
    setValue('email', emailInputValue);
  }, [emailInputValue, nameInputValue, setValue]);

  useEffect(() => {
    if (emailInputValue === '') setEmailInputValidation(translation.please_enter_an_email_address);
    else if (!emailInputValue.toLowerCase().match(emailRegex))
      setEmailInputValidation(translation.this_is_not_a_correct_email_address);
    else if (externalUsersGroups.some(({ members }) => members.some((member) => member.email === emailInputValue)))
      setEmailInputValidation(translation.email_has_already_been_added);
    else setEmailInputValidation(undefined);
  }, [
    externalUsersGroups,
    emailInputValue,
    translation.please_enter_an_email_address,
    translation.this_is_not_a_correct_email_address,
    translation.email_has_already_been_added
  ]);

  useEffect(() => {
    if (selectValue.value === null) setRoleInputValidation(translation.role_cannot_be_empty);
    else setRoleInputValidation(undefined);
  }, [emailInputValue, selectValue.value, translation.role_cannot_be_empty]);

  const handleInputExpirationDateToValue = (value: dayjs.Dayjs | null) => {
    if (value) setExpirationDateInputValue(value);
  };

  const handleSelectChange = (value: OptionProps<Role> | null) => {
    if (value) setSelectValue(value);
    else setSelectValue({ label: '', value: null });
  };

  useEffect(() => {
    handleNameInputValidation(translation, setNameInputValidation, nameInputValue);
  }, [nameInputValue, translation]);

  useEffect(() => {
    handleExpirationDateInputValidation(
      translation,
      setExpirationDateInputValidation,
      expirationDateInputValue,
      isExpirationDateEnabled
    );
  }, [expirationDateInputValue, isExpirationDateEnabled, translation]);

  useEffect(() => {
    if (isExpirationDateEnabled === undefined) setIsExpirationDateEnabled(false);
    if (expirationDateInputValue === null)
      setExpirationDateInputValue(
        drawerState.contentValue.expirationDate ? dayjs(drawerState.contentValue.expirationDate) : dayjs().add(1, 'day')
      );
  }, [drawerState.contentValue.expirationDate, expirationDateInputValue, isExpirationDateEnabled]);

  return (
    <DrawerTemplate
      title={translation.add_external_user}
      confirmButtonText={translation.add}
      id="drawer"
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={showValidation && (nameInputValidation !== undefined || emailInputValidation !== undefined)}
    >
      <AddExternalUser
        register={register}
        nameInputValue={nameInputValue}
        handleNameInputOnChange={setNameInputValue}
        nameInputValidation={nameInputValidation}
        emailInputValue={emailInputValue}
        handleEmailInputValueChange={setEmailInputValue}
        emailInputValidation={emailInputValidation}
        showValidation={showValidation}
        inputExpirationDateToValue={expirationDateInputValue}
        handleInputExpirationDateToValue={handleInputExpirationDateToValue}
        handleSwitchIsExpirationDateOnChange={() =>
          handleSwitchIsExpirationDateOnChange(
            setIsExpirationDateEnabled,
            setExpirationDateInputValue,
            expirationDateInputValue,
            isExpirationDateEnabled
          )
        }
        switchIsExpirationDateEnabled={isExpirationDateEnabled}
        handleSwitchIsAdministratorRoleEnabledOnChange={() =>
          handleSwitchIsAdministratorRoleEnableOnChange(setIsAdministratorRoleEnabled, isAdministratorRoleEnabled)
        }
        switchIsAdministratorRoleEnabled={isAdministratorRoleEnabled}
        inputExpirationDateToValueValidation={expirationDateInputValidation}
        control={control}
        selectValue={selectValue}
        selectOption={sortBy(createRoleSelectOptions(translation), (option) => option)}
        handleSelectChange={handleSelectChange}
        roleInputValidation={roleInputValidation}
        isMultiAdminEnabled={isMultiAdminEnabled}
      />
    </DrawerTemplate>
  );
};
export default AddExternalUserVariant;
